/**
 * 3D Room Visualizer — Procedural Wood Framing
 *
 * Builds 2x4 stud-and-plate wall framing OUTSIDE the room's interior
 * dimensions, so the Trusscore boards visually mount onto the framing
 * the way they would in real life.
 *
 * Coordinates are in feet. Lumber dimensions are nominal-to-actual:
 *   2x4 actual cross-section = 1.5" × 3.5" → 0.125 ft × 0.2917 ft.
 *
 * Uniform 16" stud spacing, with rough openings framed around windows and
 * doors (king studs, header, sill, cripples) when the caller supplies them.
 */

import * as THREE from 'three';
import { createLumberMaterial } from './materialFactory';
import { grainOffset, setGrainUVs } from './woodTexture';
import { FramingOmit, RoomBoardLayouts, WallFace } from './types';
import { Outline, outlineTopAt, outlineTopPieces } from '../../../calculations/sections/outline';
import { CeilingPlane } from '../../../calculations/ceiling/profile';

const STUD_THICKNESS = 1.5 / 12;   // along wall length (visible "thickness" face-on)
const STUD_DEPTH = 3.5 / 12;       // depth into the wall (away from room)
const PLATE_THICKNESS = 1.5 / 12;  // vertical thickness of top/bottom plate
const STUD_SPACING = 16 / 12;      // 16" on-center
const RAFTER_DEPTH = 5.5 / 12;     // a 2x6 on edge, above the ceiling boards
const RIDGE_DEPTH = 7.25 / 12;     // a 2x8 ridge board

/**
 * The shape of a room that is not a plain box: walls with a sloped top
 * (their outlines, in surface coordinates) and the planes of a profiled
 * ceiling, for rafters and a ridge. Everything absent = a flat-ceilinged box.
 */
export interface FramingShape {
  outlines?: Partial<Record<WallFace, Outline>>;
  planes?: CeilingPlane[];
}

/** The room's shape for framing, read off its board layouts. */
export function framingShape(layouts: RoomBoardLayouts | undefined): FramingShape | undefined {
  if (!layouts) return undefined;
  const faces: WallFace[] = ['front', 'right', 'back', 'left'];
  const outlines: Partial<Record<WallFace, Outline>> = {};
  layouts.walls.forEach((w, i) => { if (w.outline && faces[i]) outlines[faces[i]] = w.outline; });
  const planes = layouts.ceilingPlanes?.map((p) => p.plane);
  if (!Object.keys(outlines).length && !planes?.length) return undefined;
  return { outlines, planes };
}

/** A hole in a wall's framing, in that wall's surface coordinates (feet). */
export interface FramingOpening {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** A rectangle of a wall (surface coordinates) that framing is kept within: a bare area (bareFill.ts). */
export interface FramingWindow {
  x: number;
  y: number;
  width: number;
  height: number;
}

const HEADER_HEIGHT = 5.5 / 12;  // a doubled 2x6 on edge, near enough
const MIN_PIECE = 0.08;          // don't draw slivers of lumber

/** `interval` with every `cut` taken out of it. */
function cutIntervals(interval: [number, number], cuts: [number, number][]): [number, number][] {
  let pieces: [number, number][] = [interval];
  for (const [c0, c1] of cuts) {
    pieces = pieces.flatMap(([a, b]): [number, number][] => {
      if (c1 <= a || c0 >= b) return [[a, b]];
      const out: [number, number][] = [];
      if (c0 > a) out.push([a, c0]);
      if (c1 < b) out.push([c1, b]);
      return out;
    });
  }
  return pieces.filter(([a, b]) => b - a >= MIN_PIECE);
}

/**
 * Build framing for one wall in canonical local coordinates:
 *   - wall surface (Trusscore face) sits at z = 0
 *   - framing pushes OUTWARD from the room into z < 0
 *   - wall runs along +x from 0 to wallLength
 *   - studs and plates are children of the returned Group
 *
 * `openings` (in the same local coordinates) are framed as rough openings: no
 * stud runs through one — a stud that would is cut to cripples above and below
 * — a king stud stands each side, a header spans the top, a window gets a rough
 * sill, and the bottom plate is cut out under a door.
 *
 * `omit` leaves stretches unbuilt (same coordinates): a neighbouring room's
 * wall stands there. Lumber is simply stopped at the stretch — and, where only
 * the part below a height is left out, carried on above it.
 */
function buildSingleWallFraming(
  wallLength: number, wallHeight: number, openings: FramingOpening[] = [], omit: FramingOmit[] = [], outline?: Outline,
  /** Only the lumber inside these rectangles is built (each stick cut to them). Absent: the whole wall. */
  within?: FramingWindow[],
): THREE.Group {
  const group = new THREE.Group();

  if (wallLength <= 0 || wallHeight <= 0) return group;
  const inWindow = (x0: number, x1: number, y0: number, y1: number) => !within || within.some((r) => x1 > r.x && x0 < r.x + r.width && y1 > r.y && y0 < r.y + r.height);

  // The top of the wall at x: level, or the rake under a sloped ceiling.
  const topAt = (x: number) => (outline ? outlineTopAt(outline, Math.max(0, Math.min(wallLength, x))) : wallHeight);

  // A piece of lumber, less whatever falls in an omitted stretch.
  const lumber = (x0: number, x1: number, y0: number, y1: number) => {
    let pieces: [number, number, number, number][] = [[x0, x1, y0, y1]];
    for (const o of omit) {
      const top = o.below ?? Infinity;
      pieces = pieces.flatMap(([a, b, c, d]): [number, number, number, number][] => {
        if (b <= o.from || a >= o.to || c >= top) return [[a, b, c, d]];
        const kept: [number, number, number, number][] = [];
        if (a < o.from) kept.push([a, o.from, c, d]);
        if (b > o.to) kept.push([o.to, b, c, d]);
        if (d > top) kept.push([Math.max(a, o.from), Math.min(b, o.to), top, d]);
        return kept;
      });
    }
    for (const [a, b, c, d] of pieces) stick(a, b, c, d);
  };

  // Each mesh gets its own material clone so the renderer can apply
  // per-mesh opacity (depth-driven fade gradient across each wall).
  const wholeStick = (x0: number, x1: number, y0: number, y1: number) => {
    if (x1 - x0 < MIN_PIECE / 4 || y1 - y0 < MIN_PIECE / 4) return;
    const geometry = new THREE.BoxGeometry(x1 - x0, y1 - y0, STUD_DEPTH);
    place(geometry, (x0 + x1) / 2, (y0 + y1) / 2, y1 - y0 >= x1 - x0 ? 'y' : 'x', grainOffset(x0, y0));
  };
  // Kept within the windows, when there are any: the part of the stick in each.
  const stick = (x0: number, x1: number, y0: number, y1: number) => {
    if (!within) { wholeStick(x0, x1, y0, y1); return; }
    for (const r of within) {
      wholeStick(Math.max(x0, r.x), Math.min(x1, r.x + r.width), Math.max(y0, r.y), Math.min(y1, r.y + r.height));
    }
  };

  // A plate along a slope: a box the length of the slope, turned to it, its
  // underside on the line — the studs stop under it.
  const rakePlate = (x0: number, x1: number, y0: number, y1: number) => {
    const run = Math.hypot(x1 - x0, y1 - y0);
    if (run < MIN_PIECE / 4) return;
    // A sloped piece is kept whole when any of it falls in a window.
    if (!inWindow(Math.min(x0, x1), Math.max(x0, x1), Math.min(y0, y1) - PLATE_THICKNESS, Math.max(y0, y1))) return;
    const geometry = new THREE.BoxGeometry(run, PLATE_THICKNESS, STUD_DEPTH);
    geometry.translate(0, -PLATE_THICKNESS / 2, 0);
    geometry.rotateZ(Math.atan2(y1 - y0, x1 - x0));
    place(geometry, (x0 + x1) / 2, (y0 + y1) / 2, 'x', grainOffset(x0, y0));
  };

  const place = (geometry: THREE.BoxGeometry, cx: number, cy: number, grain: 'x' | 'y', pick: number) => {
    // Grain along the piece (up a stud, along a plate), each piece from its own
    // part of the texture and a shade lighter or darker than its neighbours —
    // no two sticks of lumber match.
    setGrainUVs(geometry, grain, pick * 131);
    const material = createLumberMaterial();
    material.userData.tone = (pick - 0.5) * 0.07; // kept, so a recolour keeps the stick-to-stick variation
    material.color.offsetHSL(0, 0, material.userData.tone);
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(cx, cy, -STUD_DEPTH / 2);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    group.add(mesh);
  };

  const holes = openings.filter((o) => o.width > 0 && o.height > 0);
  const reachesFloor = (o: FramingOpening) => o.y <= PLATE_THICKNESS + 0.01;

  // Stud positions (left edge of each stud): the regular 16" layout, plus a
  // king stud hard against each side of every opening.
  const studPositions: number[] = [0];
  let next = STUD_SPACING;
  while (next + STUD_THICKNESS <= wallLength - 0.01) {
    studPositions.push(next);
    next += STUD_SPACING;
  }
  const endLeft = wallLength - STUD_THICKNESS;
  if (studPositions[studPositions.length - 1] < endLeft - 0.01) {
    studPositions.push(endLeft);
  }
  for (const o of holes) {
    for (const x of [o.x - STUD_THICKNESS, o.x + o.width]) {
      const clamped = Math.min(Math.max(x, 0), endLeft);
      if (!studPositions.some((p) => Math.abs(p - clamped) < STUD_THICKNESS * 0.6)) studPositions.push(clamped);
    }
  }
  studPositions.sort((a, b) => a - b);

  // Plates — split into tiles so each tile has its own world position and own
  // material, letting the per-mesh fade gradient work continuously along the
  // plate (no horizontal banding). The bottom plate stops at a door.
  const numSegments = Math.max(1, studPositions.length);
  const segmentWidth = wallLength / numSegments;
  const doorways: [number, number][] = holes.filter(reachesFloor).map((o) => [o.x, o.x + o.width]);
  // The top plate stops at an opening that runs right up to the ceiling.
  const openToCeiling: [number, number][] = holes.filter((o) => o.y + o.height >= topAt(o.x + o.width / 2) - 0.01).map((o) => [o.x, o.x + o.width]);
  for (let i = 0; i < numSegments; i++) {
    const a = i * segmentWidth;
    const b = a + segmentWidth;
    if (!outline) for (const [p0, p1] of cutIntervals([a, b], openToCeiling)) lumber(p0, p1, wallHeight - PLATE_THICKNESS, wallHeight);
    for (const [p0, p1] of cutIntervals([a, b], doorways)) lumber(p0, p1, 0, PLATE_THICKNESS);
  }
  // Under a slope the top plate follows the rake, one piece per straight run of it.
  if (outline) {
    for (const piece of outlineTopPieces(outline)) {
      for (const [p0, p1] of cutIntervals([piece.x0, piece.x1], openToCeiling)) {
        const at = (x: number) => piece.y0 + ((x - piece.x0) / (piece.x1 - piece.x0)) * (piece.y1 - piece.y0);
        if (Math.abs(piece.y1 - piece.y0) < 1e-6) lumber(p0, p1, piece.y0 - PLATE_THICKNESS, piece.y0);
        else rakePlate(p0, p1, at(p0), at(p1));
      }
    }
  }

  // Studs — full height between the plates (to the rake, under a slope), minus
  // any opening they would cross (leaving cripples under a sill and over a header).
  for (const x of studPositions) {
    const crossing: [number, number][] = holes
      .filter((o) => x + STUD_THICKNESS > o.x + 0.01 && x < o.x + o.width - 0.01)
      .map((o) => [reachesFloor(o) ? 0 : o.y - PLATE_THICKNESS, o.y + o.height + HEADER_HEIGHT]);
    const top = Math.min(topAt(x), topAt(x + STUD_THICKNESS)) - PLATE_THICKNESS;
    for (const [y0, y1] of cutIntervals([PLATE_THICKNESS, top], crossing)) {
      lumber(x, x + STUD_THICKNESS, y0, y1);
    }
  }

  // Header over each opening, and a rough sill under each window.
  for (const o of holes) {
    const x0 = Math.max(0, o.x - STUD_THICKNESS);
    const x1 = Math.min(wallLength, o.x + o.width + STUD_THICKNESS);
    const top = o.y + o.height;
    lumber(x0, x1, top, Math.min(top + HEADER_HEIGHT, Math.min(topAt(x0), topAt(x1)) - PLATE_THICKNESS));
    if (!reachesFloor(o)) lumber(o.x, o.x + o.width, o.y - PLATE_THICKNESS, o.y);
  }

  return group;
}

/**
 * Build framing for all four walls of a room. Returns a single THREE.Group
 * containing four child groups (one per wall face), each already positioned
 * and rotated into room-local coordinates.
 *
 * The caller adds this group as a sibling of the wall mesh groups inside
 * roomGroup. The transparency controller only iterates wall groups, so
 * framing is naturally excluded from opacity sweeps.
 */
export function buildRoomFraming(
  roomLength: number,
  roomWidth: number,
  roomHeight: number,
  /** Openings per wall, in each wall's SURFACE coordinates (as the board layouts have them). */
  openings: Partial<Record<WallFace, FramingOpening[]>> = {},
  /** Stretches per wall (surface coordinates) that a neighbouring room's wall stands in for. */
  omit: Partial<Record<WallFace, FramingOmit[]>> = {},
  /** Sloped wall tops and ceiling planes, when the room is not a flat box. */
  shape: FramingShape = {},
): THREE.Group {
  const root = new THREE.Group();
  root.name = 'framing';

  const faces: { face: WallFace; length: number }[] = [
    { face: 'front', length: roomLength },
    { face: 'back', length: roomLength },
    { face: 'left', length: roomWidth },
    { face: 'right', length: roomWidth },
  ];

  for (const { face, length } of faces) {
    // Framing-local x runs the same way as the wall's surface x on the front and
    // back walls, and the opposite way on the left and right ones (their boards
    // are mirrored onto the wall; the framing is rotated onto it).
    const mirrored = face === 'left' || face === 'right';
    const holes = (openings[face] ?? []).map((o) => (mirrored ? { ...o, x: length - (o.x + o.width) } : o));
    const left = (omit[face] ?? []).map((o) => (mirrored ? { ...o, from: length - o.to, to: length - o.from } : o));
    const outline = shape.outlines?.[face];
    const localOutline: Outline | undefined = outline && mirrored ? outline.map(([x, y]): [number, number] => [length - x, y]) : outline;
    const top = outline ? Math.max(...outline.map((p) => p[1])) : roomHeight;
    const wallFraming = buildSingleWallFraming(length, top, holes, left, localOutline);
    wallFraming.name = `framing-${face}`;
    // Tag every mesh inside this wall's framing with its face so the
    // viewer can drive opacity per-face from the existing wall-opacity
    // engine (calculateWallOpacities), guaranteeing the framing of the
    // foreground wall fades exactly when its wall fades.
    wallFraming.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.userData.face = face;
        child.userData.wallProgress = 0;
      }
    });
    // Compute per-mesh wallProgress: 0 at the wall's "left" end
    // (canonical local coords, x=0) up to 1 at the wall's "right" end
    // (x=wallLength). Side-wall gradient logic uses this.
    if (length > 0.01) {
      wallFraming.children.forEach((child) => {
        if (child instanceof THREE.Mesh) {
          child.userData.wallProgress = Math.max(0, Math.min(1, child.position.x / length));
        }
      });
    }

    placeWallFraming(wallFraming, face, roomLength, roomWidth);
    root.add(wallFraming);
  }

  if (shape.planes?.some((p) => p.rise > 1e-6)) root.add(buildCeilingFraming(shape.planes));

  return root;
}

/** Turn a wall's framing (canonical coordinates: x along, framing in z < 0) onto its face of the room. */
export function placeWallFraming(group: THREE.Object3D, face: WallFace, roomLength: number, roomWidth: number): void {
  switch (face) {
    case 'front':
      // Wall surface at z = 0, framing extends to z < 0
      group.position.set(0, 0, 0);
      group.rotation.y = 0;
      break;
    case 'back':
      // Wall surface at z = roomWidth, framing extends to z > roomWidth
      group.position.set(roomLength, 0, roomWidth);
      group.rotation.y = Math.PI;
      break;
    case 'left':
      // Wall surface at x = 0, framing extends to x < 0
      group.position.set(0, 0, roomWidth);
      group.rotation.y = Math.PI / 2;
      break;
    case 'right':
      // Wall surface at x = roomLength, framing extends to x > roomLength
      group.position.set(roomLength, 0, 0);
      group.rotation.y = -Math.PI / 2;
      break;
  }
}

/**
 * The framing of a wall where it is BARE (bareFill.ts): the same studs,
 * plates, king studs, headers, sills and cripples as the room's framing, cut
 * to the bare areas, turned onto the wall. Everything in the wall's surface
 * coordinates (openings and windows as the board layout has them).
 */
export function buildWallBareFraming(
  face: WallFace, wallLength: number, wallHeight: number, openings: FramingOpening[], within: FramingWindow[], outline: Outline | undefined,
  roomLength: number, roomWidth: number,
): THREE.Group {
  // Framing-local x runs the opposite way to the surface's on the left and right walls (see buildRoomFraming).
  const mirrored = face === 'left' || face === 'right';
  const flip = <T extends { x: number; width: number }>(o: T): T => (mirrored ? { ...o, x: wallLength - (o.x + o.width) } : o);
  const localOutline: Outline | undefined = outline && mirrored ? outline.map(([x, y]): [number, number] => [wallLength - x, y]) : outline;
  const top = outline ? Math.max(...outline.map((p) => p[1])) : wallHeight;
  const group = buildSingleWallFraming(wallLength, top, openings.map(flip), [], localOutline, within.map(flip));
  group.name = 'bare-framing';
  group.userData.bareStuds = true;
  placeWallFraming(group, face, roomLength, roomWidth);
  return group;
}

/**
 * The framing over a profiled ceiling: rafters at 16" on centre up each plane
 * (2x6 on edge, standing above the boards), and a ridge board where two
 * planes meet. Tagged `face: 'ceiling'` so the viewer fades them with the
 * ceiling. Built in each plane's surface coordinates and placed by its matrix.
 */
function buildCeilingFraming(planes: CeilingPlane[]): THREE.Group {
  const root = new THREE.Group();
  root.name = 'framing-ceiling';
  const stick = (parent: THREE.Object3D, geometry: THREE.BoxGeometry, cx: number, cy: number, cz: number, grain: 'x' | 'y') => {
    const pick = grainOffset(cx, cy + cz);
    setGrainUVs(geometry, grain, pick * 131);
    const material = createLumberMaterial();
    material.userData.tone = (pick - 0.5) * 0.07;
    material.color.offsetHSL(0, 0, material.userData.tone);
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(cx, cy, cz);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.userData.face = 'ceiling';
    mesh.userData.wallProgress = 0;
    parent.add(mesh);
  };

  for (const plane of planes) {
    // Rafters run up the slope (surface y), spaced along the ridge (surface x),
    // just outside the boards (surface -z is away from the room).
    const group = new THREE.Group();
    group.matrix.fromArray(plane.matrix);
    group.matrixAutoUpdate = false;
    const positions: number[] = [0];
    let next = STUD_SPACING;
    while (next + STUD_THICKNESS <= plane.width - 0.01) { positions.push(next); next += STUD_SPACING; }
    const endLeft = plane.width - STUD_THICKNESS;
    if (positions[positions.length - 1] < endLeft - 0.01) positions.push(endLeft);
    for (const x of positions) {
      stick(group, new THREE.BoxGeometry(STUD_THICKNESS, plane.height, RAFTER_DEPTH), x + STUD_THICKNESS / 2, plane.height / 2, -STUD_DEPTH - RAFTER_DEPTH / 2, 'y');
    }
    root.add(group);
  }

  // The ridge: where two planes meet at their high edges. Along x or z.
  if (planes.length === 2) {
    const [a, b] = planes;
    const highA = topEdge(a);
    const highB = topEdge(b);
    if (highA && highB && Math.abs(highA.y - highB.y) < 1e-6) {
      const alongX = Math.abs(highA.x1 - highA.x0) > 1e-6;
      const length = alongX ? highA.x1 - highA.x0 : highA.z1 - highA.z0;
      const geometry = new THREE.BoxGeometry(alongX ? length : STUD_THICKNESS, RIDGE_DEPTH, alongX ? STUD_THICKNESS : length);
      stick(root, geometry, (highA.x0 + highA.x1) / 2, highA.y + STUD_DEPTH + RIDGE_DEPTH / 2 - RAFTER_DEPTH / 2, (highA.z0 + highA.z1) / 2, alongX ? 'x' : 'y');
    }
  }
  return root;
}

/** A plane's high edge in room coordinates (its top in surface y), or null for a flat plane. */
function topEdge(p: CeilingPlane): { x0: number; x1: number; z0: number; z1: number; y: number } | null {
  if (p.rise < 1e-6) return null;
  const m = p.matrix;
  const at = (x: number, y: number) => [m[0] * x + m[4] * y + m[12], m[1] * x + m[5] * y + m[13], m[2] * x + m[6] * y + m[14]];
  const [x0, y, z0] = at(0, p.height);
  const [x1, , z1] = at(p.width, p.height);
  return { x0: Math.min(x0, x1), x1: Math.max(x0, x1), z0: Math.min(z0, z1), z1: Math.max(z0, z1), y };
}

/**
 * Dispose all geometries in a framing group. Materials are shared across
 * framing meshes via the lumber-material singleton, so we deliberately do
 * NOT dispose them here.
 */
export function disposeFramingGroup(group: THREE.Group): void {
  group.traverse((child) => {
    if (child instanceof THREE.Mesh) {
      child.geometry?.dispose();
    }
  });
}
